import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Button,
  Table,
  Tag,
  Space,
  message,
  Spin,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Statistic,
  Image,
  Tooltip,
  Badge,
  Empty,
  Divider,
  Modal,
  Form
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  UserOutlined,
  LinkOutlined,
  PictureOutlined,
  PlusCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import dayjs from 'dayjs';
import EventFormSection from './EventFormSection';
import apiClient from '../../server/server';
import { getActiveCountries } from '../../server/configuration/FiltersServer';
import { COUNTRY_MAPPINGS, CITY_MAPPINGS } from '../../constants/locationMappings';

const { RangePicker } = DatePicker;
const { Option } = Select;

const EdmTrainTab = ({ profiles, profilesLoading, musicGenres, searchProfiles }) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([74]); // Default: Toronto
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [dateRange, setDateRange] = useState([moment(), moment().add(30, 'days')]);
  const [limit, setLimit] = useState(10);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [festivalOnly, setFestivalOnly] = useState(false);

  // Event creation modal state
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [scrapingEvent, setScrapingEvent] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [form] = Form.useForm();
  const [mediaUploadRef, setMediaUploadRef] = useState(null);
  const [videoStoryUploadRef, setVideoStoryUploadRef] = useState(null);
  const [hasScrapedAddress, setHasScrapedAddress] = useState(false);
  const [mediaUploadInProgress, setMediaUploadInProgress] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // Countries and cities state
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);

  // Search state
  const [searchResults, setSearchResults] = useState(profiles || []);
  const [searchLoading, setSearchLoading] = useState(false);

  // Canadian cities for quick selection
  const majorCities = [
    { name: 'Toronto', id: 74 },
    { name: 'Montreal', id: 89 },
    { name: 'Vancouver', id: 82 },
    { name: 'Calgary', id: 106 },
    { name: 'Edmonton', id: 112 },
    { name: 'Ottawa', id: 97 }
  ];

  // Load locations and countries on mount
  useEffect(() => {
    loadLocations();
    loadCountries();
  }, []);

  // Update search results when profiles change
  useEffect(() => {
    if (profiles && profiles.length > 0) {
      setSearchResults(profiles);
    }
  }, [profiles]);

  const loadLocations = async () => {
    try {
      setLocationsLoading(true);
      const response = await axios.get('http://localhost:3002/edmtrain/locations');
      setLocations(response.data || []);
    } catch (error) {
      message.error('Failed to load locations: ' + (error.response?.data?.message || error.message));
    } finally {
      setLocationsLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await getActiveCountries();
      const countriesData = response?.data?.data || response?.data || [];
      setCountries(countriesData);
    } catch (error) {
      console.error('Failed to load countries:', error);
      message.error('Failed to load countries');
    }
  };

  const fetchEvents = async (withTickets = false) => {
    if (selectedLocations.length === 0) {
      message.warning('Please select at least one location');
      return;
    }

    try {
      setLoading(true);
      const locationIds = selectedLocations.join(',');
      const fromDate = dateRange[0]?.format('YYYY-MM-DD');
      const toDate = dateRange[1]?.format('YYYY-MM-DD');

      let url;
      let params = {};

      if (withTickets) {
        // Use events-with-tickets endpoint
        url = 'http://localhost:3002/edmtrain/events-with-tickets';
        params = {
          locationIds,
          fromDate,
          limit
        };
      } else {
        // Use regular events endpoint
        url = 'http://localhost:3002/edmtrain/events';
        params = {
          locationIds,
          startDate: fromDate,
          endDate: toDate,
          festivalInd: festivalOnly || undefined
        };
      }

      console.log('🔍 [EDM Train] API Request:', { url, params });
      const response = await axios.get(url, { params });
      console.log('📥 [EDM Train] API Response:', response.data);

      if (withTickets) {
        // Handle events-with-tickets response
        setEvents(response.data.events || []);
        setTotalAvailable(response.data.totalAvailable || 0);
        message.success(`Loaded ${response.data.total || 0} events with ticket details from location(s): ${locationIds}`);
      } else {
        // Handle regular events response
        const mappedEvents = response.data.map(event => ({ edmTrainEvent: event, ticketDetails: { scraped: false } }));
        console.log('📊 [EDM Train] Mapped events:', mappedEvents.length, mappedEvents.slice(0, 2));
        setEvents(mappedEvents);
        setTotalAvailable(response.data.length);
        message.success(`Loaded ${response.data.length} events from location(s): ${locationIds}`);
      }
    } catch (error) {
      message.error('Failed to fetch events: ' + (error.response?.data?.message || error.message));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getScraperEndpoint = (url) => {
    if (url.includes('ticketmaster')) {
      return '/ticketmaster/scrape';
    } else if (url.includes('eventbrite')) {
      return '/eventbrite/scrape';
    } else if (url.includes('ticketweb')) {
      return '/ticketweb/scrape';
    } else if (url.includes('dice.fm')) {
      return '/dice/scrape';
    } else if (url.includes('tixr')) {
      return '/tixr/scrape';
    } else if (url.includes('ra.co') || url.includes('resident-advisor')) {
      return '/scrape';
    } else if (url.includes('tickit')) {
      return '/tickit/scrape';
    } else if (url.includes('universe')) {
      return '/universe/scrape';
    }
    return null;
  };

  const scrapeAndCreateEvent = async (ticketUrl, edmTrainEvent) => {
    try {
      setScrapingEvent(true);
      const endpoint = getScraperEndpoint(ticketUrl);

      if (!endpoint) {
        message.error('Unsupported ticketing platform');
        return;
      }

      const response = await axios.post(`http://localhost:3002${endpoint}`, { url: ticketUrl });
      const scrapedData = response.data;

      setEventData(scrapedData);
      message.success('Event scraped successfully!');

      // Map scraped genres to IDs
      const scrapedGenres = scrapedData.genres || scrapedData.lineup || [];
      const genreNameToId = {};
      musicGenres.forEach(g => { genreNameToId[g.value.toLowerCase()] = g._id; });
      const musicTypeIds = scrapedGenres
        .map(g => genreNameToId[g.toLowerCase()])
        .filter(Boolean);

      // Process dates
      let startTime = null;
      if (scrapedData.dateTime) {
        startTime = moment(scrapedData.dateTime);
        if (!startTime.isValid()) {
          startTime = moment(edmTrainEvent.date);
        }
      } else if (edmTrainEvent.date) {
        startTime = moment(edmTrainEvent.date);
      }

      let endTime = null;
      if (scrapedData.endDateTime) {
        endTime = moment(scrapedData.endDateTime);
        if (!endTime.isValid() && startTime) {
          endTime = startTime.clone().add(6, 'hours');
        }
      } else if (startTime) {
        endTime = startTime.clone().add(6, 'hours');
      }

      const publishTime = moment();

      // Auto-search for creator profile
      const searchName = scrapedData.location || scrapedData.promoter || scrapedData.organizer || edmTrainEvent.venue?.name || '';
      let autoSelectedProfileId = null;

      if (searchName && searchProfiles) {
        try {
          const profileResults = await searchProfiles(searchName);

          if (profileResults && profileResults.length > 0) {
            const normalize = (str) => str?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
            const normalizedSearch = normalize(searchName);

            const exactMatch = profileResults.find(p =>
              normalize(p.username) === normalizedSearch ||
              normalize(p.displayName) === normalizedSearch
            );

            const partialMatch = !exactMatch && profileResults.find(p => {
              const user = normalize(p.username);
              const display = normalize(p.displayName);
              return (user && (normalizedSearch.includes(user) || user.includes(normalizedSearch))) ||
                     (display && (normalizedSearch.includes(display) || display.includes(normalizedSearch)));
            });

            const matchedProfile = exactMatch || partialMatch;

            if (matchedProfile) {
              autoSelectedProfileId = matchedProfile._id;
              setSelectedProfile(matchedProfile);
              setSearchResults(profileResults);

              if (matchedProfile.musicTypes && matchedProfile.musicTypes.length > 0) {
                const profileMusicTypes = matchedProfile.musicTypes.map(mt =>
                  typeof mt === 'object' ? mt._id : mt
                );
                if (musicTypeIds.length === 0) {
                  musicTypeIds.push(...profileMusicTypes);
                }
              }

              message.success(`Auto-matched: ${matchedProfile.username || matchedProfile.displayName}`);
            }
          }
        } catch (error) {
          console.error('Error auto-searching profiles:', error);
        }
      }

      // Pre-fill the form
      const formValues = {
        title: scrapedData.name || edmTrainEvent.name,
        description: scrapedData.description || '',
        organisationName: scrapedData.promoter || scrapedData.organizer || '',
        tags: scrapedData.lineup || edmTrainEvent.artistList?.map(a => a.name) || [],
        startTime: startTime,
        endTime: endTime,
        publishTime: publishTime,
        city: null,
        country: null,
        cover: scrapedData.image,
        flyer: scrapedData.image,
        musicTypes: musicTypeIds,
        eventType: edmTrainEvent.festivalInd ? 'Festival' : 'Club',
        media: [],
        musicList: [],
        videoStory: '',
        embedmix: '',
        externalLink: ticketUrl,
        localisation: {
          address: scrapedData.address || scrapedData.location || edmTrainEvent.venue?.address || '',
          coordinates: [0, 0]
        },
        creatorProfileId: autoSelectedProfileId
      };

      const dayjsStartTime = startTime ? dayjs(startTime.format('YYYY-MM-DD HH:mm:ss')) : null;
      const dayjsEndTime = endTime ? dayjs(endTime.format('YYYY-MM-DD HH:mm:ss')) : null;

      form.setFieldsValue({
        ...formValues,
        startTime: dayjsStartTime,
        endTime: dayjsEndTime
      });

      setHasScrapedAddress(Boolean(scrapedData.address || scrapedData.location));
      setShowEventModal(true);

      // Auto-populate country/city from scraped address
      setTimeout(() => {
        const scrapedAddress = scrapedData.address || scrapedData.location;
        if (scrapedAddress) {
          const { countryId, cityId } = extractLocationFromAddress(scrapedAddress);

          if (countryId) {
            form.setFieldsValue({ country: countryId });
            const selectedCountry = countries.find(c => c._id === countryId);
            if (selectedCountry) {
              setSelectedCountryId(countryId);
              setCities(selectedCountry.cities || []);

              if (cityId) {
                form.setFieldsValue({ city: cityId });
              }
            }
          }
        }
      }, 100);
    } catch (error) {
      message.error('Failed to scrape event: ' + (error.response?.data?.message || error.message));
    } finally {
      setScrapingEvent(false);
    }
  };

  const extractLocationFromAddress = (addressString) => {
    if (!addressString || !countries.length) return { countryId: null, cityId: null };

    const countryMappings = COUNTRY_MAPPINGS;
    const parts = addressString.split(', ').map(part => part.trim());

    let countryName = null;
    let cityName = null;
    let countryCode = null;

    if (parts.length > 0) {
      const lastPart = parts[parts.length - 1];
      countryName = lastPart;
      countryCode = countryMappings[lastPart] || lastPart;
    }

    if (parts.length >= 3) {
      cityName = parts[parts.length - 3];
    } else if (parts.length >= 2) {
      cityName = parts[parts.length - 2];
    }

    const cityMappings = CITY_MAPPINGS;
    if (cityName && cityMappings[cityName]) {
      cityName = cityMappings[cityName];
    }

    let foundCountry = null;
    if (countryCode) {
      foundCountry = countries.find(c =>
        c.code === countryCode ||
        c.name.toLowerCase() === countryName?.toLowerCase()
      );
    }

    if (!foundCountry && countryName) {
      foundCountry = countries.find(c =>
        c.name.toLowerCase() === countryName.toLowerCase()
      );
    }

    let countryId = null;
    let cityId = null;

    if (foundCountry) {
      countryId = foundCountry._id;

      if (cityName && foundCountry.cities) {
        let foundCity = foundCountry.cities.find(city =>
          city.name.toLowerCase() === cityName.toLowerCase()
        );

        if (!foundCity) {
          const normalizeString = (str) => {
            return str.toLowerCase()
              .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9]/g, '');
          };

          const normalizedCityName = normalizeString(cityName);
          foundCity = foundCountry.cities.find(city =>
            normalizeString(city.name) === normalizedCityName
          );
        }

        if (!foundCity) {
          foundCity = foundCountry.cities.find(city =>
            city.name.toLowerCase().includes(cityName.toLowerCase()) ||
            cityName.toLowerCase().includes(city.name.toLowerCase())
          );
        }

        if (foundCity) {
          cityId = foundCity._id;
        }
      }
    }

    return { countryId, cityId };
  };

  const handleProfileSearch = async (value) => {
    if (!searchProfiles) return;

    if (!value || value.trim() === '') {
      setSearchResults(profiles);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    try {
      const results = await searchProfiles(value.trim());
      setSearchResults(results);
    } catch (error) {
      message.error('Failed to search profiles');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleProfileSelect = (value) => {
    const selected = searchResults.find(p => p._id === value);
    setSelectedProfile(selected);

    const currentMusicTypes = form.getFieldValue('musicTypes') || [];
    if (currentMusicTypes.length === 0 && selected?.musicTypes?.length > 0) {
      form.setFieldsValue({ musicTypes: selected.musicTypes });
    }

    const currentLocation = form.getFieldValue('localisation');
    if ((!currentLocation || !currentLocation.address) && selected?.location) {
      form.setFieldsValue({
        localisation: {
          address: selected.location,
          coordinates: [0, 0]
        }
      });
    }

    const currentCountry = form.getFieldValue('country');
    const currentCity = form.getFieldValue('city');

    if (!currentCountry && selected?.country) {
      const profileCountry = countries.find(c =>
        c._id === selected.country._id ||
        c._id === selected.country ||
        c.name === selected.country.name ||
        c.code === selected.country.code
      );

      if (profileCountry) {
        form.setFieldsValue({ country: profileCountry._id });
        setSelectedCountryId(profileCountry._id);
        setCities(profileCountry.cities || []);

        if (!currentCity && selected?.city && profileCountry.cities) {
          const profileCity = profileCountry.cities.find(city =>
            city._id === selected.city._id ||
            city._id === selected.city ||
            city.name === selected.city.name
          );

          if (profileCity) {
            form.setFieldsValue({ city: profileCity._id });
          }
        }
      }
    }
  };

  const handleCountryChange = (countryId) => {
    const selectedCountry = countries.find(country => country._id === countryId);
    if (selectedCountry) {
      setSelectedCountryId(countryId);
      setCities(selectedCountry.cities || []);
      form.setFieldsValue({ city: null });
    }
  };

  const handleAddressChange = (addressData) => {
    form.setFieldsValue({
      localisation: addressData.localisation || addressData
    });

    const addressString = addressData.localisation?.address || addressData.address;
    if (addressString) {
      const { countryId, cityId } = extractLocationFromAddress(addressString);

      if (countryId) {
        form.setFieldsValue({ country: countryId });

        const selectedCountry = countries.find(c => c._id === countryId);
        if (selectedCountry) {
          setSelectedCountryId(countryId);
          setCities(selectedCountry.cities || []);

          if (cityId) {
            form.setFieldsValue({ city: cityId });
          }

          const selectedCity = selectedCountry.cities?.find(city => city._id === cityId);
          if (selectedCity) {
            message.success(`✅ Auto-populated location: ${selectedCity.name}, ${selectedCountry.name}`);
          } else {
            message.success(`✅ Auto-populated country: ${selectedCountry.name}`);
          }
        }
      }
    }
  };

  const generateAIDescription = async () => {
    try {
      setAiLoading(true);

      const currentValues = form.getFieldsValue();
      const title = currentValues.title || '';
      const eventType = currentValues.eventType || '';
      const musicTypes = currentValues.musicTypes || [];
      const tags = currentValues.tags || [];
      const organisationName = currentValues.organisationName || '';

      const keywords = [
        title,
        eventType,
        ...musicTypes,
        ...tags,
        organisationName
      ].filter(Boolean).join(', ');

      const venue = currentValues.localisation?.address || '';

      const response = await axios.get('https://mysway.app/api/ai/generate-event-description', {
        params: {
          keywords: keywords || 'electronic music event',
          venue: venue,
          eventType: eventType || 'Club'
        }
      });

      if (response.data && response.data.description) {
        form.setFieldsValue({ description: response.data.description });
        message.success('AI description generated successfully!');
      } else {
        message.error('Failed to generate AI description');
      }
    } catch (error) {
      message.error('Failed to generate AI description. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateEvent = async (values) => {
    try {
      if (mediaUploadInProgress) {
        message.warning('Media upload already in progress. Please wait...');
        return;
      }

      setLoading(true);
      setMediaUploadInProgress(true);

      let mediaUrls = [];
      if (mediaUploadRef && mediaUploadRef.uploadPendingFiles) {
        try {
          mediaUrls = await mediaUploadRef.uploadPendingFiles();
          mediaUrls = [...new Set(mediaUrls.filter(Boolean))];
        } catch (mediaError) {
          message.error('Failed to upload media files. Please try again.');
          setLoading(false);
          setMediaUploadInProgress(false);
          return;
        }
      }

      let videoStoryUrl = values.videoStory || '';
      if (videoStoryUploadRef && videoStoryUploadRef.uploadPendingFiles) {
        try {
          const videoStoryUrls = await videoStoryUploadRef.uploadPendingFiles();
          if (videoStoryUrls.length > 0) {
            videoStoryUrl = videoStoryUrls[0];
          }
        } catch (videoError) {
          message.error('Failed to upload video story. Please try again.');
          setLoading(false);
          setMediaUploadInProgress(false);
          return;
        }
      }

      let flyerUrl = values.flyer || '';

      if (flyerUrl && !flyerUrl.includes('api.decadance.app')) {
        try {
          const requestPayload = {
            imageUrl: flyerUrl,
            timestamp: Date.now(),
            _cacheBust: Math.random()
          };

          const uploadResponse = await apiClient.post('/uploads/from-url', requestPayload);

          if (uploadResponse.data && uploadResponse.data.data && uploadResponse.data.data.url) {
            flyerUrl = uploadResponse.data.data.url;
          } else {
            throw new Error('Invalid response format from image upload');
          }
        } catch (uploadErr) {
          message.error(`Failed to upload flyer to backend: ${uploadErr.response?.statusText || uploadErr.message}`);
          setLoading(false);
          setMediaUploadInProgress(false);
          return;
        }
      }

      const eventPayload = {
        ...values,
        isPublished: true,
        localisation: values.localisation || {
          coordinates: [0, 0],
          address: values.address || '',
        },
        isPrivate: false,
        includeChat: true,
        startTime: values.startTime?.toISOString(),
        endTime: values.endTime?.toISOString(),
        publishTime: values.publishTime?.toISOString(),
        musicTypes: Array.isArray(values.musicTypes) ? values.musicTypes : [],
        tags: Array.isArray(values.tags) ? values.tags : [],
        affiliatedProfiles: Array.isArray(values.affiliatedProfiles) ? values.affiliatedProfiles : [],
        media: [...new Set([...(Array.isArray(values.media) ? values.media : []), ...mediaUrls])],
        musicList: Array.isArray(values.musicList) ? values.musicList : [],
        flyer: flyerUrl,
        videoStory: videoStoryUrl,
        externalLink: values.externalLink || '',
        embedmix: values.embedmix || '',
        eventType: values.eventType || 'Club',
        description: values.description || '',
        organisationName: values.organisationName || '',
        cover: values.cover || null,
      };

      if (eventPayload.localisation?.localisation) {
        eventPayload.localisation = eventPayload.localisation.localisation;
      }

      const response = await apiClient.post('/backoffice/events/create', eventPayload);
      message.success('Event created successfully!');
      setShowEventModal(false);
      setHasScrapedAddress(false);
      setEventData(null);
      setMediaUploadRef(null);
      setVideoStoryUploadRef(null);
      form.resetFields();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      message.error(`Failed to create event: ${errorMessage}`);
    } finally {
      setLoading(false);
      setMediaUploadInProgress(false);
    }
  };

  const columns = [
    {
      title: 'Event Info',
      key: 'event',
      width: 200,
      render: (_, record) => {
        const event = record.edmTrainEvent;
        const tickets = record.ticketDetails;
        const imgUrl = tickets.scraped ? tickets.data?.image : null;

        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {imgUrl && (
              <Image
                src={imgUrl}
                width={40}
                height={40}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                preview
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
              />
            )}
            <div style={{ fontWeight: 600, fontSize: 12 }}>{event.name}</div>
            <Space size={2} wrap>
              {event.festivalInd && <Tag color="purple" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>Festival</Tag>}
              {event.livestreamInd && <Tag color="blue" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>Live</Tag>}
              {event.electronicGenreInd && <Tag color="cyan" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>EDM</Tag>}
              {event.ages && <Tag color="orange" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{event.ages}</Tag>}
            </Space>
          </Space>
        );
      }
    },
    {
      title: 'Date & Time',
      key: 'date',
      width: 120,
      render: (_, record) => {
        const event = record.edmTrainEvent;
        const tickets = record.ticketDetails;
        const dateTime = tickets.scraped ? tickets.data?.dateTime : event.date;

        return (
          <Space direction="vertical" size={2}>
            <div style={{ fontSize: 12 }}>
              <CalendarOutlined /> {moment(dateTime).format('MMM DD, YYYY')}
            </div>
            {event.startTime && (
              <div style={{ fontSize: 11, color: '#666' }}>
                🕐 {event.startTime}
              </div>
            )}
            {event.endTime && (
              <div style={{ fontSize: 11, color: '#666' }}>
                → {event.endTime}
              </div>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Venue & Location',
      key: 'venue',
      width: 170,
      render: (_, record) => {
        const event = record.edmTrainEvent;
        const tickets = record.ticketDetails;

        return (
          <Space direction="vertical" size={1}>
            <div style={{ fontWeight: 500, fontSize: 12 }}>
              {tickets.scraped ? tickets.data?.location : event.venue?.name}
            </div>
            <div style={{ fontSize: 10, color: '#666' }}>
              <EnvironmentOutlined /> {tickets.scraped
                ? `${tickets.data?.city || ''}, ${tickets.data?.region || ''}`
                : event.venue?.location}
            </div>
            {event.venue?.address && (
              <div style={{ fontSize: 9, color: '#999' }}>
                {event.venue.address}
              </div>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Artists / Lineup',
      key: 'artists',
      width: 180,
      render: (_, record) => {
        const event = record.edmTrainEvent;
        const artists = event.artistList || [];

        if (artists.length === 0) return <Tag style={{ margin: 0 }}>No artists listed</Tag>;

        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {artists.slice(0, 4).map(artist => (
              <div key={artist.id} style={{ fontSize: 12 }}>
                <UserOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                <a href={artist.link} target="_blank" rel="noopener noreferrer" style={{ color: '#333' }}>
                  {artist.name}
                </a>
              </div>
            ))}
            {artists.length > 4 && (
              <Tag color="geekblue" style={{ margin: 0, fontSize: 11 }}>
                +{artists.length - 4} more artists
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Platform',
      key: 'platform',
      width: 100,
      render: (_, record) => {
        const tickets = record.ticketDetails;
        const event = record.edmTrainEvent;

        // Detect platform from ticket link
        let platform = 'Unknown';
        if (event.ticketLink) {
          if (event.ticketLink.includes('ticketmaster')) platform = 'TM';
          else if (event.ticketLink.includes('eventbrite')) platform = 'EB';
          else if (event.ticketLink.includes('ticketweb')) platform = 'TW';
          else if (event.ticketLink.includes('dice.fm')) platform = 'Dice';
          else if (event.ticketLink.includes('tixr')) platform = 'Tixr';
          else if (event.ticketLink.includes('ra.co')) platform = 'RA';
          else if (event.ticketLink.includes('tickit')) platform = 'Tickit';
          else if (event.ticketLink.includes('universe')) platform = 'Univ';
        }

        return (
          <Space direction="vertical" size={3}>
            <Badge
              status={tickets.scraped ? 'success' : 'default'}
              text={<span style={{ fontSize: 10 }}>{tickets.scraped ? 'Scraped' : 'No'}</span>}
            />
            {platform !== 'Unknown' && (
              <Tag color={tickets.scraped ? 'green' : 'default'} style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>
                {platform}
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, record) => {
        const event = record.edmTrainEvent;

        return (
          <Space direction="vertical" size="small">
            {event.ticketLink && (
              <Button
                size="small"
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={() => scrapeAndCreateEvent(event.ticketLink, event)}
                loading={scrapingEvent}
                block
              >
                Scrape & Create
              </Button>
            )}
            {event.link && (
              <Button
                size="small"
                icon={<LinkOutlined />}
                href={event.link}
                target="_blank"
                block
              >
                EDM Train
              </Button>
            )}
            {event.ticketLink && (
              <Button
                size="small"
                icon={<LinkOutlined />}
                href={event.ticketLink}
                target="_blank"
                block
              >
                View Tickets
              </Button>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            <span>EDM Train Event Manager</span>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={loadLocations}
            loading={locationsLoading}
          >
            Refresh Locations
          </Button>
        }
      >
        {/* Quick City Selection */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            Quick Select - Major Canadian Cities (Ctrl+Click to add multiple):
            <Tag color="blue" style={{ marginLeft: 8 }}>
              Selected IDs: {selectedLocations.join(', ') || 'None'}
            </Tag>
          </div>
          <Space wrap>
            {majorCities.map(city => (
              <Button
                key={city.id}
                type={selectedLocations.includes(city.id) ? 'primary' : 'default'}
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey) {
                    // Ctrl/Cmd+Click: Toggle this city (add/remove)
                    if (selectedLocations.includes(city.id)) {
                      setSelectedLocations(selectedLocations.filter(id => id !== city.id));
                    } else {
                      setSelectedLocations([...selectedLocations, city.id]);
                    }
                  } else {
                    // Regular click: Replace with this city only
                    console.log(`🏙️ [EDM Train] Selected city: ${city.name} (ID: ${city.id})`);
                    setSelectedLocations([city.id]);
                  }
                }}
              >
                {city.name} ({city.id})
              </Button>
            ))}
            <Button
              danger
              onClick={() => setSelectedLocations([])}
            >
              Clear All
            </Button>
          </Space>
        </div>

        <Divider />

        {/* Search Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <div style={{ marginBottom: 8 }}>Locations:</div>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Select locations"
              value={selectedLocations}
              onChange={setSelectedLocations}
              loading={locationsLoading}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {locations.map(loc => (
                <Option key={loc.id} value={loc.id}>
                  {loc.city}, {loc.stateCode} ({loc.countryCode})
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={8}>
            <div style={{ marginBottom: 8 }}>Date Range:</div>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
            />
          </Col>

          <Col span={4}>
            <div style={{ marginBottom: 8 }}>Scrape Limit:</div>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={50}
              value={limit}
              onChange={setLimit}
            />
          </Col>

          <Col span={4}>
            <div style={{ marginBottom: 8 }}>Festival Only:</div>
            <Select
              style={{ width: '100%' }}
              value={festivalOnly}
              onChange={setFestivalOnly}
            >
              <Option value={false}>All Events</Option>
              <Option value={true}>Festivals Only</Option>
            </Select>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => fetchEvents(false)}
            loading={loading}
          >
            Search Events
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={() => fetchEvents(true)}
            loading={loading}
            danger
            disabled
          >
            Search with Ticket Scraping (Coming Soon)
          </Button>
        </Space>

        {/* Stats */}
        {events.length > 0 && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic title="Events Loaded" value={events.length} />
            </Col>
            <Col span={8}>
              <Statistic title="Total Available" value={totalAvailable} />
            </Col>
            <Col span={8}>
              <Statistic
                title="Events with Tickets"
                value={events.filter(e => e.ticketDetails.scraped).length}
              />
            </Col>
          </Row>
        )}

        {/* Events Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" tip="Loading events..." />
          </div>
        ) : events.length > 0 ? (
          <Table
            columns={columns}
            dataSource={events}
            rowKey={(record) => record.edmTrainEvent.id}
            scroll={{ x: 1100 }}
            pagination={{
              pageSize: 20,
              showTotal: (total) => `Total ${total} events`,
              showSizeChanger: true
            }}
            size="small"
          />
        ) : (
          <Empty description="No events found. Use the search filters above to find events." />
        )}
      </Card>

      {/* Event Creation Modal */}
      <Modal
        title="Create Event from EDM Train"
        open={showEventModal}
        onCancel={() => {
          setShowEventModal(false);
          setEventData(null);
          setHasScrapedAddress(false);
          setMediaUploadRef(null);
          setVideoStoryUploadRef(null);
          form.resetFields();
        }}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <EventFormSection
          form={form}
          loading={loading}
          handleCreateEvent={handleCreateEvent}
          setShowEventForm={setShowEventModal}
          setHasScrapedAddress={setHasScrapedAddress}
          setMediaUploadRef={setMediaUploadRef}
          setVideoStoryUploadRef={setVideoStoryUploadRef}
          selectedProfile={selectedProfile}
          searchResults={searchResults}
          profilesLoading={profilesLoading}
          searchLoading={searchLoading}
          handleProfileSearch={handleProfileSearch}
          handleProfileSelect={handleProfileSelect}
          countries={countries}
          cities={cities}
          handleCountryChange={handleCountryChange}
          handleAddressChange={handleAddressChange}
          hasScrapedAddress={hasScrapedAddress}
          musicGenres={musicGenres}
          aiLoading={aiLoading}
          generateAIDescription={generateAIDescription}
        />
      </Modal>
    </div>
  );
};

export default EdmTrainTab;
